import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Stethoscope, 
  Brain, 
  Heart, 
  Baby, 
  Activity,
  Pill,
  Eye,
  Bone,
  TrendingUp,
  ArrowLeft,
  ArrowRight,
  Droplet,
  Droplets,
  Wind,
  Ear,
  Siren,
  Atom,
  Zap,
  HeartPulse,
  Sparkles,
  UserRound,
  FlaskConical,
  Scissors,
  Scan,
  Waves,
  Filter,
  Footprints
} from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { supabase } from "@/integrations/supabase/client";

const SpecialtiesGrid = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [carouselApi, setCarouselApi] = useState<any>(null);
  const [specialties, setSpecialties] = useState<Array<{
    name: string;
    slug: string;
    icon: any;
    gradient: string;
    jobCount: number;
  }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMetiersWithJobs();
  }, []);

  const loadMetiersWithJobs = async () => {
    try {
      setLoading(true);
      const { data: metiers } = await supabase
        .from('metiers')
        .select('title, slug, jobs_count, icon_name, gradient_colors')
        .eq('is_active', true)
        .gt('jobs_count', 0)
        .order('jobs_count', { ascending: false })
        .limit(8);

      if (metiers) {
        const iconMap: Record<string, any> = {
          'Stethoscope': Stethoscope,
          'Brain': Brain,
          'Heart': Heart,
          'Baby': Baby,
          'Activity': Activity,
          'Pill': Pill,
          'Eye': Eye,
          'Bone': Bone,
          'Droplet': Droplet,
          'Droplets': Droplets,
          'Wind': Wind,
          'Ear': Ear,
          'Siren': Siren,
          'Atom': Atom,
          'Zap': Zap,
          'HeartPulse': HeartPulse,
          'Sparkles': Sparkles,
          'UserRound': UserRound,
          'FlaskConical': FlaskConical,
          'Scissors': Scissors,
          'Scan': Scan,
          'Waves': Waves,
          'Filter': Filter,
          'Footprints': Footprints,
        };

        const gradientMap: Record<string, string> = {
          'from-primary to-primary-light': 'from-primary to-primary-light',
          'from-secondary-light to-accent-light': 'from-secondary-light to-accent-light',
          'from-primary to-secondary': 'from-primary to-secondary',
          'from-secondary to-accent': 'from-secondary to-accent',
          'from-secondary to-secondary-light': 'from-secondary to-secondary-light',
          'from-accent-light to-secondary': 'from-accent-light to-secondary',
          'from-primary-light to-secondary': 'from-primary-light to-secondary',
          'from-accent to-accent-light': 'from-accent to-accent-light',
        };

        const formattedSpecialties = metiers.map(metier => ({
          name: metier.title,
          slug: metier.slug,
          icon: iconMap[metier.icon_name || 'Stethoscope'] || Stethoscope,
          gradient: metier.gradient_colors || 'from-primary to-secondary',
          jobCount: metier.jobs_count || 0,
        }));

        setSpecialties(formattedSpecialties);
      }
    } catch (error) {
      console.error('Error loading specialties with jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (carouselApi) {
    carouselApi.on('select', () => {
      setCurrentSlide(carouselApi.selectedScrollSnap());
    });
  }

  if (loading) {
    return (
      <section className="py-20 md:py-28 bg-gradient-to-b from-white to-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground mt-4">Chargement des métiers...</p>
          </div>
        </div>
      </section>
    );
  }

  if (specialties.length === 0) {
    return null;
  }

  return (
    <section className="py-20 md:py-28 bg-gradient-to-b from-white to-muted/20 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-white/80 backdrop-blur-sm rounded-full mb-6 border border-primary/10 shadow-lg">
            <TrendingUp className="w-4 h-4 text-primary" />
            <span className="text-primary font-semibold text-sm tracking-wide">OPPORTUNITÉS</span>
          </div>
          <h2 className="font-poppins font-black text-3xl md:text-5xl bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-4">
            Les métiers qui recrutent
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Découvrez les spécialités les plus demandées et trouvez votre prochaine mission
          </p>
        </div>


        {/* Mobile Carousel */}
        <div className="lg:hidden mb-12">
          <Carousel 
            className="w-full max-w-sm mx-auto"
            setApi={setCarouselApi}
            opts={{
              align: "center",
              loop: true,
            }}
          >
            <CarouselContent>
              {specialties.map((specialty, index) => (
                <CarouselItem key={index} className="basis-1/2">
                  <Link
                    to={`/metiers/${specialty.slug}`}
                    className="group relative block p-1"
                  >
                    <div className={`absolute -inset-0.5 bg-gradient-to-br ${specialty.gradient} rounded-2xl opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-500`}></div>
                    
                    <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl border border-border/50 hover:border-primary/30 hover:shadow-2xl transition-all duration-500 p-6 text-center group-hover:scale-105">
                      <div className={`w-16 h-16 bg-gradient-to-br ${specialty.gradient} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                        <specialty.icon className="w-8 h-8 text-white" strokeWidth={2.5} />
                      </div>
                      <h3 className="font-semibold text-foreground mb-2 text-sm group-hover:text-primary transition-colors">
                        {specialty.name}
                      </h3>
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full border border-primary/20">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse"></div>
                        <p className="text-xs font-bold text-primary">
                          {specialty.jobCount} poste{specialty.jobCount > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </Link>
                </CarouselItem>
              ))}
            </CarouselContent>
            
            <CarouselPrevious className="left-2 h-10 w-10 bg-white/90 backdrop-blur-sm text-primary hover:bg-primary hover:text-white shadow-lg border border-primary/20">
              <ArrowLeft className="h-5 w-5" />
            </CarouselPrevious>
            <CarouselNext className="right-2 h-10 w-10 bg-white/90 backdrop-blur-sm text-primary hover:bg-primary hover:text-white shadow-lg border border-primary/20">
              <ArrowRight className="h-5 w-5" />
            </CarouselNext>
          </Carousel>
          
          <div className="flex justify-center gap-3 mt-6">
            {specialties.map((_, index) => (
              <button
                key={index}
                onClick={() => carouselApi?.scrollTo(Math.floor(index / 2))}
                className={`p-2 rounded-full transition-all duration-300 ${
                  Math.floor(currentSlide * 2) === index || Math.floor(currentSlide * 2) + 1 === index
                    ? 'bg-primary' 
                    : 'bg-transparent'
                }`}
                aria-label={`Aller à ${specialties[index].name}`}
              >
                <div className={`h-2 w-2 rounded-full transition-all duration-300 ${
                  Math.floor(currentSlide * 2) === index || Math.floor(currentSlide * 2) + 1 === index
                    ? 'bg-white scale-125' 
                    : 'bg-muted-foreground/30'
                }`} />
              </button>
            ))}
          </div>
        </div>

        {/* Desktop Grid */}
        <div className="hidden lg:grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 max-w-6xl mx-auto mb-12">
          {specialties.map((specialty, index) => (
            <Link
              key={index}
              to={`/metiers/${specialty.slug}`}
              className="group relative animate-fade-in"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {/* Glow effect */}
              <div className={`absolute -inset-0.5 bg-gradient-to-br ${specialty.gradient} rounded-2xl opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-500`}></div>
              
              {/* Card */}
              <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl border border-border/50 hover:border-primary/30 hover:shadow-2xl transition-all duration-500 p-6 text-center group-hover:scale-105">
                <div className={`w-16 h-16 bg-gradient-to-br ${specialty.gradient} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                  <specialty.icon className="w-8 h-8 text-white" strokeWidth={2.5} />
                </div>
                <h3 className="font-semibold text-foreground mb-2 text-sm md:text-base group-hover:text-primary transition-colors">
                  {specialty.name}
                </h3>
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full border border-primary/20">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse"></div>
                  <p className="text-xs font-bold text-primary">
                    {specialty.jobCount} poste{specialty.jobCount > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/metiers"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/60 backdrop-blur-sm border border-primary/20 rounded-xl text-primary hover:bg-primary hover:text-white font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl group"
          >
            <span>Voir tous les métiers</span>
            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SpecialtiesGrid;